import { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  useDeletePrivateNotifMutation,
  useReadNotifMutation
} from '@/app/common/api/notifications/inappApi';
import { successMessage } from '@/utils/toast';
import { useNotifications } from '@/hooks/useNotification';
import { Link } from 'react-router-dom';
import { Badge, Card, Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import FalconCardHeader from '@/components/common/FalconCardHeader';
import NotificationList from './NotificationList';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import type { NotificationChannel } from '@/app/common/api/notifications/types/inappApiTypes';

export interface NotificationDropdownProps {
  token: string | undefined;
}

const NotificationDropdown = ({ token }: NotificationDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<NotificationChannel>('private');
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [deleteNotification] = useDeletePrivateNotifMutation();
  const [readNotification] = useReadNotifMutation();
  const {
    notifications,
    setNotifications,
    setCountUnreadByChannel,
    countUnreadMsg,
    hasMore,
    isFetching,
    fetchNextPage
  } = useNotifications({
    appName: 'mizfa_tools',
    // تا زمانی که access token وجود ندارد، init و generate_token اجرا نشود
    enabled: !!token
  });

  const handleToggle = () => setIsOpen(!isOpen);
  const handleMarkAllRead = (channel: NotificationChannel) => {
    readNotification({
      message_id: '',
      read_all: true,
      channel
    })
      .unwrap()
      .then(() => {
        setNotifications(prev => {
          const updatedChannel = prev[channel].map(n => ({
            ...n,
            is_read: true
          }));
          return {
            ...prev,
            [channel]: updatedChannel
          };
        });
        setCountUnreadByChannel(prev => ({
          ...prev,
          [channel]: 0
        }));
        successMessage(
          `همه اعلان‌های ${channel === 'public' ? 'عمومی' : 'خصوصی'} خوانده شدند`
        );
      });
  };

  const handleDeleteAllPrivate = () => {
    deleteNotification({
      message_id: '',
      delete_all: true,
      channel: 'private'
    })
      .unwrap()
      .then(() => {
        setNotifications(prev => ({
          ...prev,
          private: []
        }));
        setCountUnreadByChannel(prev => ({ ...prev, private: 0 }));
        successMessage('همه اعلان‌های خصوصی حذف شدند');
      });
  };

  if (!token) return null;

  return (
    <Dropdown navbar as="li" show={isOpen} onToggle={handleToggle}>
      <Dropdown.Toggle
        bsPrefix="toggle"
        as={Link}
        to="#!"
        className={classNames('px-0  nav-link position-relative')}
      >
        {countUnreadMsg > 0 && (
          <Badge
            style={{ width: 16, height: 16, top: 8, left: 0 }}
            className="position-absolute fw-bold d-flex align-items-center justify-content-center"
            pill
            bg="danger"
          >
            {countUnreadMsg}
          </Badge>
        )}
        <ChildrensTooltip
          delayToRemove={100}
          placement="bottom"
          showOnHover={false}
          html={'اعلانات'}
        >
          <FontAwesomeIcon icon="bell" transform="shrink-6" className="fs-4" />
        </ChildrensTooltip>
      </Dropdown.Toggle>
      <Dropdown.Menu className="dropdown-menu-card dropdown-menu-end dropdown-caret dropdown-caret-bg">
        <Card
          className="dropdown-menu-notification dropdown-menu-end shadow-none"
          style={{ minWidth: '17rem' }}
        >
          <FalconCardHeader
            className="card-header"
            title="اعلانات"
            titleTag="h6"
            light={false}
            endEl={
              <div className="d-flex gap-2">
                <Link
                  className="card-link fs--2 fw-normal"
                  to="#!"
                  onClick={() => handleMarkAllRead(activeTab)}
                >
                  خواندن همه
                </Link>
                {activeTab === 'private' && (
                  <Link
                    className="card-link fs--2 text-danger fw-normal"
                    to="#!"
                    onClick={handleDeleteAllPrivate}
                  >
                    حذف همه
                  </Link>
                )}
              </div>
            }
          />
          <NotificationList
            notifications={notifications}
            hasMore={hasMore}
            isFetching={isFetching}
            fetchNextPage={fetchNextPage}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            scrollContainerRef={scrollContainerRef}
            deleteNotification={deleteNotification}
            readNotification={readNotification}
            setNotifications={setNotifications}
            setCountUnreadByChannel={setCountUnreadByChannel}
            successMessage={successMessage}
          />
        </Card>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default NotificationDropdown;
